import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/base/SEOHead';

export default function TexasComptrollerPage() {
  const services = [
    {
      title: 'Sales and Use Tax Permit',
      description: 'Register for a sales tax permit to collect and remit Texas sales tax',
      icon: 'ri-shopping-cart-line',
      details: [
        'Required for businesses selling taxable goods or services',
        'Free to obtain through Webfile system',
        'Must file returns monthly, quarterly, or annually',
        'Penalties apply for late filing or payment'
      ]
    },
    {
      title: 'Franchise Tax',
      description: 'Annual tax levied on businesses chartered or doing business in Texas',
      icon: 'ri-building-line',
      details: [
        'Applies to most business entities except sole proprietorships',
        'Based on taxable margin calculation',
        'Annual report due May 15th',
        'No tax owed if revenue under $1.23 million'
      ]
    },
    {
      title: 'Employer Tax Accounts',
      description: 'Register for unemployment insurance and payroll tax obligations',
      icon: 'ri-team-line',
      details: [
        'Required if you have employees in Texas',
        'Unemployment insurance tax registration',
        'Quarterly wage reports required',
        'Online filing through Webfile system'
      ]
    },
    {
      title: 'Mixed Beverage Tax',
      description: 'Special tax permits for businesses selling alcoholic beverages',
      icon: 'ri-goblet-line',
      details: [
        'Required for restaurants and bars serving alcohol',
        '8.25% gross receipts tax on mixed beverages',
        'Monthly reporting and payment required',
        'Separate from sales tax permit'
      ]
    }
  ];

  const webfileFeatures = [
    'File and pay sales tax returns online',
    'Register for new tax permits',
    'Update business information',
    'View filing history and payment records',
    'Set up payment plans for outstanding balances',
    'Download tax forms and publications'
  ];

  const publications = [
    {
      title: 'Texas Tax Publications',
      items: [
        'Publication 94-101: Texas Sales and Use Tax',
        'Publication 96-122: Franchise Tax Overview',
        'Publication 96-1045: Exemption Certificates',
        'Publication 94-117: Manufacturing Exemptions'
      ]
    },
    {
      title: 'Industry-Specific Guides',
      items: [
        'Restaurant and Food Service Tax Guide',
        'Construction Industry Tax Guide',
        'Retail Sales Tax Guide',
        'Healthcare Provider Tax Guide'
      ]
    }
  ];

  const importantDeadlines = [
    { date: 'January 20', event: 'December sales tax due (monthly filers)' },
    { date: 'January 31', event: 'Q4 sales tax due (quarterly filers)' },
    { date: 'May 15', event: 'Franchise tax annual report due' },
    { date: '20th of each month', event: 'Previous month sales tax due' }
  ];

  return (
    <>
      <SEOHead
        title="Texas Comptroller Tax Registration & Business Resources"
        description="Complete guide to Texas Comptroller tax registration and business resources. Learn about sales tax permits, franchise tax, employer taxes, and online filing through the Texas Comptroller's office."
        canonical="/regulations/texas-comptroller"
        keywords="Texas Comptroller, Texas sales tax permit, franchise tax Texas, employer taxes, Texas tax registration, Webfile Texas"
        ogImage="https://public.readdy.ai/ai/img_res/2cf9f21eb9bc3266280d16649e4af09c.jpg"
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        <main className="pt-32 pb-20">
          <div className="max-w-7xl mx-auto px-6">
            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb" className="mb-8">
              <Link to="/regulations" className="text-[#D2691E] hover:underline text-sm font-medium">
                ← Back to Regulations
              </Link>
            </nav>

            {/* Breadcrumb Structured Data */}
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://elpasotexasbusiness.com/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Regulations",
                    "item": "https://elpasotexasbusiness.com/regulations"
                  },
                  {
                    "@type": "ListItem",
                    "position": 3,
                    "name": "Texas Comptroller",
                    "item": "https://elpasotexasbusiness.com/regulations/texas-comptroller"
                  }
                ]
              })}
            </script>

            {/* Header */}
            <div className="mb-12">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-[#1B3A3A] to-[#2C5F4F] rounded-2xl flex items-center justify-center">
                  <i className="ri-government-line text-3xl text-white"></i>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-gray-900">Texas Comptroller</h1>
                  <p className="text-lg text-gray-600 mt-1">State tax permits and compliance information</p>
                </div>
              </div>
              <p className="text-base text-gray-700 leading-relaxed max-w-4xl">
                The Texas Comptroller of Public Accounts is responsible for collecting state taxes, administering tax permits, 
                and ensuring compliance with Texas tax laws. All businesses operating in Texas must register with the Comptroller 
                for applicable tax permits and file regular tax returns.
              </p>
            </div>

            {/* Tax Services Grid */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Tax Services &amp; Permits</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {services.map((service, index) => (
                  <div key={index} className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-all">
                    <div className="flex items-start space-x-4 mb-4">
                      <div className="w-12 h-12 bg-[#1B3A3A] rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${service.icon} text-xl text-white`}></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{service.description}</p>
                      </div>
                    </div>
                    <ul className="space-y-2 ml-16">
                      {service.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start space-x-2">
                          <i className="ri-checkbox-circle-fill text-[#2C5F4F] text-sm mt-1 flex-shrink-0"></i>
                          <span className="text-sm text-gray-700 leading-relaxed">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Webfile System */}
            <div className="mb-16 bg-gradient-to-br from-[#FFE8D6] to-[#FFD4B8] rounded-3xl p-10">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-[#1B3A3A] rounded-xl flex items-center justify-center">
                  <i className="ri-computer-line text-xl text-white"></i>
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Webfile Online System</h2>
              </div>
              <p className="text-base text-gray-700 leading-relaxed mb-6">
                Webfile is the Texas Comptroller's free online system for managing your tax accounts, filing returns, 
                and making payments. All businesses are encouraged to use Webfile for faster processing and immediate confirmation.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {webfileFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3 bg-white/60 rounded-xl p-4">
                    <i className="ri-check-double-line text-[#2C5F4F] text-xl"></i>
                    <span className="text-sm font-medium text-gray-800">{feature}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <a
                  href="https://comptroller.texas.gov/taxes/file-pay/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 bg-[#1B3A3A] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#2C5F4F] transition-colors whitespace-nowrap cursor-pointer"
                >
                  <span>Access Webfile System</span>
                  <i className="ri-external-link-line"></i>
                </a>
              </div>
            </div>

            {/* Publications & Resources */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Tax Publications &amp; Guides</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {publications.map((category, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-2xl p-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">{category.title}</h3>
                    <ul className="space-y-3">
                      {category.items.map((item, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <i className="ri-file-text-line text-[#2C5F4F] text-lg mt-0.5"></i>
                          <span className="text-sm text-gray-700 leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Important Deadlines */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Important Tax Deadlines</h2>
              <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-8">
                <div className="space-y-4">
                  {importantDeadlines.map((deadline, index) => (
                    <div key={index} className="flex items-center space-x-4 pb-4 border-b border-gray-200 last:border-0 last:pb-0">
                      <div className="w-24 flex-shrink-0">
                        <div className="bg-[#1B3A3A] text-white text-center py-2 px-3 rounded-lg">
                          <span className="text-sm font-bold whitespace-nowrap">{deadline.date}</span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 leading-relaxed">{deadline.event}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-sm text-gray-600 leading-relaxed">
                    <strong>Note:</strong> If a deadline falls on a weekend or holiday, the due date is the next business day. 
                    Late filing or payment may result in penalties and interest charges.
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="mb-16 bg-white border border-gray-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Taxpayer Assistance</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Contact Information</h3>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <i className="ri-phone-line text-[#2C5F4F] text-lg mt-0.5"></i>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Toll-Free</p>
                        <p className="text-sm text-gray-700">1-800-252-5555</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-mail-line text-[#2C5F4F] text-lg mt-0.5"></i>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Email</p>
                        <p className="text-sm text-gray-700">tax.help@cpa.texas.gov</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-time-line text-[#2C5F4F] text-lg mt-0.5"></i>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Hours</p>
                        <p className="text-sm text-gray-700">Monday - Friday, 8:00 AM - 5:00 PM CT</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Official Website</h3>
                  <p className="text-sm text-gray-700 leading-relaxed mb-4">
                    Visit the Texas Comptroller website for forms, publications, online filing, and detailed tax information.
                  </p>
                  <a
                    href="https://comptroller.texas.gov"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 text-[#1B3A3A] font-semibold hover:text-[#2C5F4F] transition-colors cursor-pointer"
                  >
                    <span>comptroller.texas.gov</span>
                    <i className="ri-external-link-line"></i>
                  </a>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-br from-[#1B3A3A] to-[#2C5F4F] rounded-3xl p-10 text-center">
              <h2 className="text-3xl font-bold text-white mb-4">Need Help with Tax Compliance?</h2>
              <p className="text-base text-white/90 leading-relaxed mb-6 max-w-2xl mx-auto">
                Our team can help you understand your tax obligations and ensure your business stays compliant 
                with all Texas Comptroller requirements.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center space-x-2 bg-[#FFE8D6] text-[#1B3A3A] px-8 py-4 rounded-xl font-bold hover:bg-white transition-colors whitespace-nowrap cursor-pointer"
              >
                <span>Contact Us</span>
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}